import * as React from "react";
import {
  Button,
  Card,
  Checkbox,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Typography,
} from "@mui/material";

import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";

export interface AuidioTrackMappingCardProps {
  id: number;
  version: string;
  versionOptions: { value: string; display: string }[];
  language: string;
  languageOptions: { value: string; display: string }[];
  position: string;
  positionOptions: { value: string; display: string }[];
  sampling: number;
  bitDepth: number;
  format: string;
  formatOptions: { value: string; display: string }[];
  r128: boolean;
  dolbyE: boolean;
  loudness: number;
  lra: number;
  truePeaks: number;
  disableSave: boolean;
  onEditing: (values: {
    version: string;
    language: string;
    position: string;
    format: string;
    r128: boolean;
    dolbyE: boolean;
    sampling: number;
    bitDepth: number;
    loudness: number;
    lra: number;
    truePeaks: number;
    id: number;
  }) => void;
}

const AudioTrackMappingCard: React.FC<AuidioTrackMappingCardProps> = ({
  id,
  version,
  versionOptions,
  language,
  languageOptions,
  position,
  positionOptions,
  sampling,
  bitDepth,
  format,
  formatOptions,
  r128,
  dolbyE,
  loudness,
  lra,
  truePeaks,
  disableSave,
  onEditing,
}) => {
  const { t } = useTranslation();
  const [versionValue, setVersionValue] = React.useState<string>(version);
  const [languageValue, setLanguageValue] = React.useState<string>(language);
  const [positionValue, setPositionValue] = React.useState<string>(position);
  const [samplingValue, setSamplingValue] = React.useState<number>(sampling);
  const [bitDepthValue, setBitDepthValue] = React.useState<number>(bitDepth);
  const [formatValue, setFormatValue] = React.useState<string>(format);
  const [r128Value, setR128Value] = React.useState<boolean>(r128);
  const [dolbyEValue, setDolbyEValue] = React.useState<boolean>(dolbyE);
  const [hasBeenEdited, setHasBeenEdited] = React.useState<boolean>(false);
  const [loudnessValue, setLoudnessValue] = React.useState<number>(loudness);
  const [lraValue, setLraValue] = React.useState<number>(lra);
  const [truePeaksValue, setTruePeaksValue] = React.useState<number>(truePeaks);

  const handleSelectChange = (setter: React.Dispatch<React.SetStateAction<string>>) => {
    return (e: SelectChangeEvent) => {
      setter(e.target.value);
    };
  };

  const handleCheckboxChange = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.checked);
    };
  };

  React.useEffect(() => {
    const hasVersionBeenChanged = versionValue !== version;
    const hasLanguageBeenChanged = languageValue !== language;
    const hasPositionBeenChanged = positionValue !== position;
    const hasFormatBeenChanged = formatValue !== format;
    const hasR128BeenChanged = r128Value !== r128;
    const hasDolbyEBeenChanged = dolbyEValue !== dolbyE;
    const hasSamplingBeenChanged = samplingValue !== sampling;
    const hasBitDepthEBeenChanged = bitDepthValue !== bitDepth;
    const hasLoudnessBeenChanged = loudnessValue !== loudness;
    const hasLraEBeenChanged = lraValue !== lra;
    const hasTruePeaksBeenChanged = truePeaksValue !== truePeaks;

    if (
      hasVersionBeenChanged ||
      hasLanguageBeenChanged ||
      hasPositionBeenChanged ||
      hasFormatBeenChanged ||
      hasR128BeenChanged ||
      hasDolbyEBeenChanged ||
      hasLoudnessBeenChanged ||
      hasLraEBeenChanged ||
      hasTruePeaksBeenChanged ||
      hasSamplingBeenChanged ||
      hasBitDepthEBeenChanged
    ) {
      setHasBeenEdited(true);
    } else {
      setHasBeenEdited(false);
    }
  }, [
    dolbyE,
    dolbyEValue,
    format,
    formatValue,
    language,
    languageValue,
    position,
    positionValue,
    r128,
    r128Value,
    version,
    versionValue,
    sampling,
    samplingValue,
    bitDepth,
    bitDepthValue,
    loudness,
    loudnessValue,
    lra,
    lraValue,
    truePeaks,
    truePeaksValue,
  ]);

  const handleCancelClick: React.MouseEventHandler = () => {
    setVersionValue(version);
    setLanguageValue(language);
    setPositionValue(position);
    setFormatValue(format);
    setR128Value(r128);
    setDolbyEValue(dolbyE);
    setSamplingValue(sampling);
    setBitDepthValue(bitDepth);
    setLoudnessValue(loudness);
    setLraValue(lra);
    setTruePeaksValue(truePeaks);
  };

  const handleSaveChangesClick: React.MouseEventHandler = () => {
    onEditing({
      version: versionValue,
      language: languageValue,
      position: positionValue,
      format: formatValue,
      r128: r128Value,
      dolbyE: dolbyEValue,
      sampling: samplingValue,
      bitDepth: bitDepthValue,
      loudness: loudnessValue,
      lra: lraValue,
      truePeaks: truePeaksValue,
      id: id,
    });
  };

  return (
    <Card sx={{ pt: 2, pb: 2, pr: 3, pl: 3 }} variant="outlined">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} sx={{ height: "32px" }}>
        <Typography fontWeight={600} variant="subtitle2" color="info.main">
          ID : {id}
        </Typography>
        <Actions hasBeenEdited={!disableSave && hasBeenEdited}>
          <Button sx={{ mr: 1.5 }} size="small" startIcon={<ClearIcon />} onClick={handleCancelClick}>
            {t("common:cancel")}
          </Button>
          <Button size="small" variant="contained" disabled={disableSave} onClick={handleSaveChangesClick}>
            {t("button:saveChanges")}
          </Button>
        </Actions>
      </Stack>
      <Grid container spacing={2}>
        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} sx={{ color: "text.tier", mb: 0.5 }}>
            {t("common:version")}
          </Typography>
          <Select
            value={versionValue}
            onChange={handleSelectChange(setVersionValue)}
            size="small"
            readOnly={disableSave}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          >
            {versionOptions.map((versionOption) => (
              <MenuItem key={versionOption.value} value={versionOption.value}>
                {versionOption.display}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} color="text.tier" mb={0.5}>
            {t("common:language")}
          </Typography>
          <Select
            value={languageValue}
            onChange={handleSelectChange(setLanguageValue)}
            size="small"
            readOnly={disableSave}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          >
            {languageOptions.map((languageOption) => (
              <MenuItem key={languageOption.value} value={languageOption.value}>
                {languageOption.display}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} sx={{ color: "text.tier", mb: 0.5 }}>
            {t("common:position")}
          </Typography>
          <Select
            value={positionValue}
            onChange={handleSelectChange(setPositionValue)}
            size="small"
            readOnly={disableSave}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          >
            {positionOptions.map((positionOption) => (
              <MenuItem key={positionOption.value} value={positionOption.value}>
                {positionOption.display}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} color="text.tier" mb={0.5}>
            {t("common:sampling")}
          </Typography>
          <OutlinedInput
            readOnly={disableSave}
            value={samplingValue}
            size="small"
            type={"number"}
            onChange={(e) => setSamplingValue(parseInt(e.target.value))}
          />
        </Grid>
        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} color="text.tier" mb={0.5}>
            {t("common:bitDepth")}
          </Typography>
          <OutlinedInput
            readOnly={disableSave}
            value={bitDepthValue}
            size="small"
            type={"number"}
            onChange={(e) => setBitDepthValue(parseInt(e.target.value))}
          />
        </Grid>
        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} color="text.tier" mb={0.5}>
            {t("common:format")}
          </Typography>
          <Select
            readOnly={disableSave}
            value={formatValue}
            onChange={handleSelectChange(setFormatValue)}
            size="small"
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          >
            {formatOptions.map((formatOption) => (
              <MenuItem key={formatOption.value} value={formatOption.value}>
                {formatOption.display}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} color="text.tier" mb={0.5}>
            R128
          </Typography>

          <Checkbox
            checked={r128Value}
            disabled={disableSave}
            onChange={handleCheckboxChange(setR128Value)}
            sx={{ ml: "-10px" }}
          />
        </Grid>
        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} color="text.tier" mb={0.5}>
            "Dolby-E"
          </Typography>
          <Checkbox
            disabled={disableSave}
            checked={dolbyEValue}
            onChange={handleCheckboxChange(setDolbyEValue)}
            sx={{ ml: "-10px" }}
          />
        </Grid>

        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} color="text.tier" mb={0.5}>
            {t("common:loudness")}
          </Typography>
          <OutlinedInput
            readOnly={disableSave}
            value={loudnessValue}
            size="small"
            type={"number"}
            onChange={(e) => setLoudnessValue(parseInt(e.target.value))}
          />
        </Grid>
        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} color="text.tier" mb={0.5}>
            LRA
          </Typography>
          <OutlinedInput
            readOnly={disableSave}
            value={lraValue}
            size="small"
            type={"number"}
            onChange={(e) => setLraValue(parseInt(e.target.value))}
          />
        </Grid>
        <Grid item md={3}>
          <Typography variant="body2" fontWeight={500} color="text.tier" mb={0.5}>
            {t("common:truePeaks")}
          </Typography>
          <OutlinedInput
            readOnly={disableSave}
            value={truePeaksValue}
            size="small"
            type={"number"}
            onChange={(e) => setTruePeaksValue(parseInt(e.target.value))}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

export default AudioTrackMappingCard;

const Actions = styled("div")<{ hasBeenEdited: boolean }>(({ theme, hasBeenEdited }) => ({
  opacity: 0,
  marginRight: `-${theme.spacing(1)}`,
  transition: theme.transitions.create("opacity", {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.easeInOut,
  }),
  ...(hasBeenEdited && {
    opacity: 1,
  }),
}));
